import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl';
import type {
	DeleteFieldMessages,
	EditIssueTypeMessages,
	FieldsEditorMessages,
	NavigationMessages,
	RequiredFieldMessages,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features';

/**
 * Creates a dummy MessageDescriptorV2 to be used in mock tests and stories only.
 * It will not translate.
 *
 * @param defaultMessage will be the human redable string
 */
export const mockMessageDescriptor = (defaultMessage: string): MessageDescriptor => ({
	id: `${defaultMessage}.mock-message.do-not-translate`,
	defaultMessage,
});

export const exampleDeleteFieldMessages: DeleteFieldMessages = {
	buttonConfirm: mockMessageDescriptor('buttonConfirm'),
	buttonCancel: mockMessageDescriptor('buttonCancel'),
	modalHeader: mockMessageDescriptor('modalHeader'),
	issueDataWipeWarning: mockMessageDescriptor('issueDataWipeWarning'),
	eventualConsistencyWarning: mockMessageDescriptor('eventualConsistencyWarning'),
	noUndo: mockMessageDescriptor('noUndo'),
	warningWhenOneIssueType: mockMessageDescriptor('warningWhenOneIssueType'),
	warningWhenMultipleIssueTypes: mockMessageDescriptor('warningWhenMultipleIssueTypes'),
};

export const exampleNavigationMessages: NavigationMessages = {
	issueTypesLink: mockMessageDescriptor('issueTypesLink'),
	addIssueTypeDisabledTooltip: mockMessageDescriptor('addIssueTypeDisabledTooltip'),
};

export const exampleEditIssueTypeMessages: EditIssueTypeMessages = {
	editIssueTypeSuccessTitle: mockMessageDescriptor('editIssueTypeSuccessTitle'),
	editIssueTypeSuccessDescription: mockMessageDescriptor('editIssueTypeSuccessDescription'),
	backToProject: mockMessageDescriptor('backToProject'),
	editIssueTypeFailureTitle: mockMessageDescriptor('editIssueTypeFailureTitle'),
	editIssueTypeFailureDescription: mockMessageDescriptor('editIssueTypeFailureDescription'),
	descriptionLabel: mockMessageDescriptor('descriptionLabel'),
	editDescriptionButtonLabel: mockMessageDescriptor('editDescriptionButtonLabel'),
	descriptionPlaceholder: mockMessageDescriptor('descriptionPlaceholder'),
	avatarAlt: mockMessageDescriptor('avatarAlt'),
	avatarAccessibleAlt: mockMessageDescriptor('avatarAccessibleAlt'),
	avatarTooltipContent: mockMessageDescriptor('avatarTooltipContent'),
	avatarAriaLabel: mockMessageDescriptor('avatarAriaLabel'),
	editAvatar: mockMessageDescriptor('editAvatar'),
};

export const exampleFieldsEditorMessages: FieldsEditorMessages = {
	sidebarHelperText: mockMessageDescriptor('sidebarHelperText'),
	removeDropzoneHeader: mockMessageDescriptor('removeDropzoneHeader'),
	removeDropzoneDescription: mockMessageDescriptor('removeDropzoneDescription'),
	noActionFieldToolbarHeader: mockMessageDescriptor('noActionFieldToolbarHeader'),
	noActionFieldToolbarDescription: mockMessageDescriptor('noActionFieldToolbarDescription'),
	noActionFieldToolbarDescriptionAppField: mockMessageDescriptor(
		'noActionFieldToolbarDescriptionAppField',
	),
	inactiveFieldsEmptyMessage: mockMessageDescriptor('inactiveFieldsEmptyMessage'),
	errorMessageTitle: mockMessageDescriptor('errorMessageTitle'),
	errorMessage: mockMessageDescriptor('errorMessage'),
	refreshButton: mockMessageDescriptor('refreshButton'),
	sharedFieldInfoMessage: mockMessageDescriptor('sharedFieldInfoMessage'),
	sharedFieldDialogMessage: mockMessageDescriptor('sharedFieldDialogMessage'),
	sharedFieldLinkToDocumentationText: mockMessageDescriptor('sharedFieldLinkToDocumentationText'),
};

export const exampleRequiredFieldMessages: RequiredFieldMessages = {
	required: mockMessageDescriptor('required'),
	requiredTooltip: mockMessageDescriptor('requiredTooltip'),
	requiredDisabledAndCheckedTooltip: mockMessageDescriptor('requiredDisabledAndCheckedTooltip'),
	requiredDisabledAndUncheckedTooltip: mockMessageDescriptor('requiredDisabledAndUncheckedTooltip'),
	requiredDisabledInAllCategoriesTooltip: mockMessageDescriptor(
		'requiredDisabledInAllCategoriesTooltip',
	),
	requiredDisabledInCategoryTooltip: mockMessageDescriptor('requiredDisabledInCategoryTooltip'),
};
